import Cookies from 'universal-cookie';
import { isTokenValid } from '../auth';

/**
 * Cookie ベースのトークン管理
 * react-cookie を使用してトークンを管理
 */

interface TokenStore {
  authToken: string | null;
  expiresAt: number | null;
}

class TokenManager {
  private cookies: Cookies;
  private readonly AUTH_TOKEN_KEY = 'auth_token';

  constructor() {
    this.cookies = new Cookies();
  }

  /**
   * 認証トークンを保存
   * Cookie に保存（有効期限付き）
   */
  setAuthToken(token: string, expiresInSeconds?: number): void {
    const options: {
      path: string;
      expires?: Date;
      sameSite: 'strict' | 'lax' | 'none';
      secure?: boolean;
    } = {
      path: '/',
      sameSite: 'strict',
    };

    // 有効期限を設定（デフォルト: 24時間）
    const expiresIn = expiresInSeconds || 60 * 60 * 24; // 24時間
    options.expires = new Date(Date.now() + expiresIn * 1000);

    // HTTPS環境では secure フラグを設定
    if (window.location.protocol === 'https:') {
      options.secure = true;
    }

    // 認証トークンを保存
    this.cookies.set(this.AUTH_TOKEN_KEY, token, options);
  }

  /**
   * 認証トークンを取得
   */
  getAuthToken(): string | null {
    const token = this.cookies.get(this.AUTH_TOKEN_KEY);
    return token || null;
  }

  /**
   * すべてのトークンをクリア
   */
  clearTokens(): void {
    this.cookies.remove(this.AUTH_TOKEN_KEY, { path: '/' });
  }

  /**
   * トークンが有効かチェック
   *
   * UX改善のため、JWT の有効期限もチェックします。
   * ただし、セキュリティはバックエンドが担保します（フロントエンドの検証は補助的）。
   */
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    if (!token) return false;

    // JWT の有効期限もチェック（UX改善）
    if (!isTokenValid(token)) {
      // 期限切れトークンはクリア
      this.clearTokens();
      return false;
    }

    return true;
  }

  /**
   * Cookie インスタンスを取得（react-cookie との統合用）
   */
  getCookiesInstance(): Cookies {
    return this.cookies;
  }
}

// シングルトンインスタンス
export const tokenManager = new TokenManager();

// 型エクスポート
export type { TokenStore };
